import { useState } from "react";
import { Link } from "react-router";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto, UserDto } from "@/api/types";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { User01Icon } from "@/components/Icons/User01Icon";
import { ImagePlaceholder } from "@/components/ImagePlaceholder/ImagePlaceholder";
import { buttonVariants } from "@/design-system/Button";
import { usePostHog } from "@/external/posthog-client";
import { twResolve } from "@/helpers/tw-resolve";
import { useSlug } from "@/hooks/useSlug";
import { useForbiddenPopupContext } from "@/providers/ForbiddenPopupProvider";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type UserAvatarProps = {
  image: ImageDto | undefined;
  /** Defaults to `"user avatar"`; use an empty string when adjacent text already names the subject. */
  alt?: string;
  /** Keeps the avatar frame while allowing another entity type to supply its established empty-image icon. */
  fallbackIcon?: React.ReactNode;
  isUserDeleted?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
};

const avatarIconSizes: Record<NonNullable<UserAvatarProps["size"]>, number> = {
  xs: 15,
  sm: 20,
  md: 25,
  lg: 40,
};

/**
 * Renders a user avatar image with a `"user avatar"` label by default.
 * Pass an empty `alt` when the image is decorative because adjacent text already supplies the subject's name.
 */
export function UserAvatar({
  image,
  alt = "user avatar",
  fallbackIcon,
  isUserDeleted,
  size = "md",
}: UserAvatarProps): React.ReactNode {
  const imageId = image?.id;

  return (
    <UserAvatarContent
      key={imageId}
      image={image}
      alt={alt}
      fallbackIcon={fallbackIcon}
      isUserDeleted={isUserDeleted}
      size={size}
    />
  );
}

function UserAvatarContent({
  image,
  alt = "user avatar",
  fallbackIcon,
  isUserDeleted,
  size = "md",
}: UserAvatarProps): React.ReactNode {
  const { t } = useTranslation();
  const [imageState, setImageState] = useState<"loading" | "loaded" | "missing" | "errored">(
    image ? "loading" : "missing",
  );

  const resolveImage = useImageResolver();

  let imageAssetSize: UserAvatarProps["size"];
  switch (size) {
    case "lg":
      imageAssetSize = "md";
      break;

    case "xs":
    case "sm":
    case "md":
    default:
      imageAssetSize = "sm";
      break;
  }

  return (
    <div
      className={twResolve(
        "relative aspect-square shrink-0 rounded-full border border-border-default object-cover",
        isUserDeleted && "opacity-40",
        size === "xs" && "size-icon-prominent",
        size === "sm" && "size-icon-display",
        size === "md" && "size-control-md",
        size === "lg" && "size-brand-mark",
      )}
    >
      {/* Image */}
      {image && imageState !== "errored" ? (
        <div className="relative size-full overflow-hidden rounded-full">
          {imageState === "loading" && <ImagePlaceholder image={image} />}
          <img
            className="relative size-full rounded-full bg-background-default object-cover select-none"
            src={resolveImage(image, imageAssetSize)}
            alt={alt}
            loading="lazy"
            onLoad={() => setImageState("loaded")}
            onError={() => setImageState("errored")}
          />
        </div>
      ) : (
        <span className="block size-full rounded-full bg-background-default">
          <span className="flex size-full items-center justify-center">
            {fallbackIcon ?? <User01Icon tone="secondary" size={avatarIconSizes[size]} />}
          </span>
        </span>
      )}

      {isUserDeleted && (
        <div
          role="img"
          aria-label={t("common.label.user-status.deleted")}
          className="absolute top-0 right-0 flex size-icon-prominent translate-x-1/3 -translate-y-1/3 items-center justify-center rounded-full bg-background-danger-secondary text-text-on-danger-secondary"
        >
          <Trash01Icon />
        </div>
      )}
    </div>
  );
}

export type UserAvatarLinkProps = {
  user: UserDto;
  size?: UserAvatarProps["size"];
};

export function UserAvatarLink({ user, size = "md" }: UserAvatarLinkProps): React.ReactNode {
  const slug = useSlug();
  const postHog = usePostHog();
  const forbiddenContext = useForbiddenPopupContext();

  if (user.canViewProfile) {
    return (
      <Link to={routes.users.details({ slug, id: user.id })} onClick={() => postHog.capture("clicked_neighbour")}>
        <UserAvatar size={size} image={user.avatar} isUserDeleted={!!user.deletedAt} />
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={twResolve(buttonVariants({ variant: "ghostSecondary", size: "sm" }), "h-auto p-0")}
      onClick={forbiddenContext.open}
    >
      <UserAvatar size={size} image={user.avatar} isUserDeleted={!!user.deletedAt} />
    </button>
  );
}
